import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { BrowserQRCodeReader, IScannerControls } from '@zxing/browser';
import { api } from '../../services/api';
import '../../styles/visit-scanner.css';

export type Visit = { id: string; customerId: string; customerName: string; customerEmail: string; previousVisits: number; visits: number; completed: boolean; actorEmail: string; createdAt: string };
type Customer = { id: string; name: string; email: string; visits: number; visitsRequired: number; completedCards: number; lastVisitAt: string | null; complete: boolean };
type Lookup = { code: string; customer: Customer; business: { id: string; name: string; visitsRequired: number } };
type Outcome = 'visit' | 'reward';

export function VisitScanner({ token, onRegistered }: { token: string; onRegistered: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<IScannerControls | undefined>(undefined);
  const streamRef = useRef<MediaStream | null>(null);
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [lookup, setLookup] = useState<Lookup>();
  const [outcome, setOutcome] = useState<Outcome>();
  const [history, setHistory] = useState<Visit[]>([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const loadHistory = useCallback(() => api<{ visits: Visit[] }>('/visits', token).then((result) => setHistory(result.visits)).catch(() => undefined), [token]);
  useEffect(() => {
    void loadHistory();
    return () => {
      controlsRef.current?.stop();
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, [loadHistory]);

  function stopScanner() {
    controlsRef.current?.stop();
    controlsRef.current = undefined;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setScanning(false);
  }

  async function findCustomer(code: string) {
    const normalized = code.trim(); if (!normalized || loading) return;
    stopScanner(); setLoading(true); setMessage('');
    try { setLookup(await api<Lookup>(`/visits/lookup?code=${encodeURIComponent(normalized)}`, token)); setManualCode(normalized); }
    catch (failure) { setLookup(undefined); setMessage(failure instanceof Error ? failure.message : 'No pudimos leer la tarjeta.'); }
    finally { setLoading(false); }
  }

  async function startScanner() {
    setLookup(undefined); setOutcome(undefined); setMessage('');

    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setMessage('La cámara requiere una conexión segura. En este dispositivo abre Argox con HTTPS; una dirección http://192.168… no puede pedir permiso de cámara.');
      return;
    }

    try {
      stopScanner();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: 'environment' } },
      });
      streamRef.current = stream;
      setScanning(true);
      const reader = new BrowserQRCodeReader();
      controlsRef.current = await reader.decodeFromStream(stream, videoRef.current!, (result) => {
        if (result) void findCustomer(result.getText());
      });
    } catch (failure) {
      stopScanner();
      const errorName = failure instanceof DOMException ? failure.name : '';
      if (errorName === 'NotAllowedError' || errorName === 'SecurityError') {
        setMessage('El permiso de cámara está bloqueado. Actívalo para este sitio en la configuración del navegador y vuelve a intentar.');
      } else if (errorName === 'NotFoundError' || errorName === 'OverconstrainedError') {
        setMessage('No encontramos una cámara disponible en este dispositivo. También puedes escribir el código manualmente.');
      } else {
        setMessage('No pudimos iniciar la cámara. Cierra cualquier app que la esté usando y vuelve a intentar.');
      }
    }
  }

  async function confirmVisit() {
    if (!lookup || loading) return; setLoading(true); setMessage('');
    try {
      const result = await api<{ message: string; customer: Customer }>('/visits', token, { method: 'POST', body: JSON.stringify({ code: lookup.code, idempotencyKey: crypto.randomUUID() }) });
      setLookup({ ...lookup, customer: result.customer }); setOutcome('visit'); await loadHistory(); onRegistered();
    } catch (failure) { setMessage(failure instanceof Error ? failure.message : 'No pudimos registrar la visita.'); }
    finally { setLoading(false); }
  }

  async function redeemReward() {
    if (!lookup || loading) return; setLoading(true); setMessage('');
    try { const result = await api<{ message: string; customer: Customer }>('/rewards/redeem', token, { method: 'POST', body: JSON.stringify({ code: lookup.code, idempotencyKey: crypto.randomUUID() }) }); setLookup({ ...lookup, customer: result.customer }); setOutcome('reward'); await loadHistory(); onRegistered(); }
    catch (failure) { setMessage(failure instanceof Error ? failure.message : 'No pudimos entregar la recompensa.'); }
    finally { setLoading(false); }
  }

  function reset() { setLookup(undefined); setOutcome(undefined); setManualCode(''); setMessage(''); stopScanner(); }
  function scanNext() { reset(); void startScanner(); }
  function submitManual(event: FormEvent) { event.preventDefault(); void findCustomer(manualCode); }

  return <div className="visits-page">
    <div className="visits-heading"><div><span>OPERACIÓN EN CAJA</span><h1>Registrar una visita</h1><p>Escanea el QR del cliente y confirma el sello.</p></div>{!scanning && !lookup && <button className="action-3d" onClick={() => void startScanner()}>▣ Abrir cámara</button>}</div>
    <div className="visits-layout"><section className="scanner-card">
      {outcome && lookup ? <div className="visit-success">
        <div className="success-badge">{outcome === 'reward' ? '★' : '✓'}</div>
        <h2>{outcome === 'reward' ? '¡Recompensa entregada!' : '¡Sello registrado!'}</h2>
        <p>{lookup.customer.name} · {outcome === 'reward' ? 'Empezó un nuevo ciclo' : `${lookup.customer.visits} de ${lookup.customer.visitsRequired} visitas`}</p>
        <div className="visit-progress"><div><span>Progreso actual</span><strong>{lookup.customer.visits} de {lookup.customer.visitsRequired}</strong></div><div>{Array.from({ length: lookup.customer.visitsRequired }, (_, index) => <i key={index} className={index < lookup.customer.visits ? 'complete' : ''}>{index < lookup.customer.visits ? '✓' : ''}</i>)}</div></div>
        <button className="action-3d scan-next" onClick={scanNext}>Escanear siguiente cliente →</button>
      </div> : !lookup ? <>
        <div className={scanning ? 'camera-frame active' : 'camera-frame'}><video ref={videoRef} muted playsInline /><div className="camera-target"><i /><i /><i /><i /></div>{!scanning && <div className="camera-empty"><b>▦</b><strong>Escanea una tarjeta Argox</strong><span>Usaremos la cámara trasera de este dispositivo.</span></div>}</div>
        {scanning && <button type="button" className="stop-scanner" onClick={stopScanner}>✕ Detener cámara</button>}
        <form className="manual-code" onSubmit={submitManual}><label>{scanning ? '¿No lee bien? Busca por correo o código' : 'O busca por correo o código'}<input value={manualCode} onChange={(event) => setManualCode(event.target.value)} placeholder="correo@cliente.com o ARGOX:negocio:cliente" /></label><button className="action-3d" disabled={loading}>{loading ? 'Buscando…' : 'Buscar'}</button></form>
      </> : <div className="visit-confirm">
        <div className="customer-avatar">{lookup.customer.name.split(/\s+/).map((word) => word[0]).slice(0, 2).join('').toUpperCase()}</div><small>CLIENTE ENCONTRADO</small><h2>{lookup.customer.name}</h2><p>{lookup.customer.email}</p>
        <div className="visit-progress"><div><span>Progreso actual</span><strong>{lookup.customer.visits} de {lookup.customer.visitsRequired}</strong></div><div>{Array.from({ length: lookup.customer.visitsRequired }, (_, index) => <i key={index} className={index < lookup.customer.visits ? 'complete' : ''}>{index < lookup.customer.visits ? '✓' : ''}</i>)}</div></div>
        {lookup.customer.complete ? <><div className="visit-complete">★ Tarjeta completa · {lookup.customer.visits} visitas</div><div className="confirm-actions"><button className="cancel" onClick={reset}>Cancelar</button><button className="action-3d confirm reward" onClick={() => void redeemReward()} disabled={loading}>{loading ? 'Entregando…' : 'Entregar recompensa ★'}</button></div></> : <div className="confirm-actions"><button className="cancel" onClick={reset}>Cancelar</button><button className="action-3d confirm" onClick={() => void confirmVisit()} disabled={loading}>{loading ? 'Registrando…' : `Confirmar visita ${lookup.customer.visits + 1} →`}</button></div>}
      </div>}
      {message && <div className={message.includes('registrada') || message.includes('completada') ? 'visit-message success' : 'visit-message'}>{message}</div>}
    </section><aside className="visit-history"><div><span>ACTIVIDAD RECIENTE</span><button onClick={() => void loadHistory()}>↻</button></div>{history.length ? history.map((visit) => { const removed = visit.visits < visit.previousVisits; return <article key={visit.id}><div><b>{visit.customerName}</b><small>{visit.customerEmail}</small></div><strong className={removed ? 'removed' : ''}>{removed ? '−1' : '+1'}</strong><time>{new Date(visit.createdAt).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}</time></article>; }) : <div className="empty-history"><b>Sin visitas todavía</b><span>La primera aparecerá aquí.</span></div>}</aside></div>
  </div>;
}
